/**
 * InfoTip.js
 *
 * A small ⓘ button that explains a setting. Opens on hover (mouse) or tap,
 * closes when the mouse leaves, on a tap anywhere else, or Escape.
 */

import React, { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";

export default function InfoTip({ label, children }) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapper = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const close = (e) => { if (!wrapper.current?.contains(e.target)) setIsOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setIsOpen(false); };
        document.addEventListener("pointerdown", close);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("pointerdown", close);
            document.removeEventListener("keydown", onKey);
        };
    }, [isOpen]);

    return (
        <span
            ref={wrapper}
            className="relative inline-flex align-middle"
            // Hover only for a mouse: on phones a tap would open it on hover, then close it on click
            onPointerEnter={(e) => { if (e.pointerType === "mouse") setIsOpen(true); }}
            onPointerLeave={(e) => { if (e.pointerType === "mouse") setIsOpen(false); }}
        >
            <button
                type="button"
                aria-label={`About ${label}`}
                aria-expanded={isOpen}
                onClick={(e) => { e.preventDefault(); setIsOpen(true); }}
                className="ml-1.5 text-zinc-500 hover:text-yellow focus:text-yellow focus:outline-none"
            >
                <Info size={16} />
            </button>
            {isOpen && (
                <span
                    role="tooltip"
                    className="absolute left-1/2 top-full z-30 mt-2 w-72 max-w-[80vw] -translate-x-1/2 rounded-xl border border-zinc-700 bg-zinc-900 p-4 text-left text-sm font-normal normal-case tracking-normal leading-relaxed text-zinc-200 shadow-2xl"
                >
                    {children}
                </span>
            )}
        </span>
    );
}
